import { styled } from "@mui/material";
import { api, types } from "api";
import { InfoSnackBar } from "components/InfoSnackBar";
import { ProfileLayout } from "components/ProfileLayout";
import { passwordAcceptable, passwordsMatch } from "helpers/authValidation";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { ERROR_PASSWORD_UPDATE, SUCCESS_PASSWORD_UPDATE } from "messages";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { getPath, routes } from "routes";
import { ChangePasswordPageForm } from "./ChangePasswordPageForm";

const PREFIX = "ChangePasswordPage";

const classes = {
	root: `${PREFIX}-root`,
};

const Root = styled("div")(() => ({
	[`&.${classes.root}`]: {
		height: "100%",
	},
}));

export const ChangePasswordPage = (): JSX.Element => {
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [submit, setSubmit] = useState(false);
	const [err, setErr] = useState(false);
	const params = useParams<{ userId: types.UUID }>();

	const [user, setUser] = useState<types.User | null>(null);

	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const history = useHistory();

	// Get the user id
	useEffect(() => {
		const query = params.userId
			? api.getUserDetails(params.userId)
			: api.getMyUserData();
		query
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				setErr(false);
				setUser(res.data);

				if (params.userId) {
					updateLastBreadcrumbs([
						{
							name: `${res.data.firstName} ${res.data.lastName}`,
							params: { userId: params.userId },
						},
						undefined,
					]);
				}
			})
			.catch(() => {
				setErr(true);
			});
	}, [params.userId, updateLastBreadcrumbs]);

	// Update the password
	const onSubmit = (event: React.FormEvent): void => {
		event.preventDefault();

		if (
			!(
				passwordAcceptable(password) &&
				passwordsMatch(password, confirmPassword)
			)
		) {
			setErr(true);
			setSubmit(true);
			return;
		}

		if (user?.userId === undefined) {
			setErr(true);
			setSubmit(true);
			return;
		}

		api
			.updateUser(user.userId, { password })
			.then((res) => {
				if (res.status !== 200) {
					setErr(true);
					setSubmit(true);
					return;
				}
				history.push(
					params.userId
						? getPath(routes._.people._.userProfile, { userId: params.userId })
						: routes._.people._.me.path,
				);
			})
			.catch(() => {
				setErr(true);
				setSubmit(true);
			});
	};

	return (
		<Root className={classes.root}>
			<InfoSnackBar
				isOpen={submit}
				onClose={() => setSubmit(false)}
				message={err ? ERROR_PASSWORD_UPDATE : SUCCESS_PASSWORD_UPDATE}
				severity={err ? "error" : "success"}
			/>

			<ProfileLayout user={user}>
				<ChangePasswordPageForm
					userIdParam={params.userId}
					password={password}
					setPassword={setPassword}
					confirmPassword={confirmPassword}
					setConfirmPassword={setConfirmPassword}
					onSubmit={onSubmit}
				/>
			</ProfileLayout>
		</Root>
	);
};
